import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import MonitoringFilters, {
  MonitoringFilterScope,
  useMonitoringFilterQuery,
} from '../../components/workforceMonitoring/MonitoringFilters';
import SortableHeader from '../../components/workforceMonitoring/SortableHeader';
import { SortDir, sortRows, toggleSort } from '../../utils/tableSort';

export interface ApplicationsPageProps {
  scope?: MonitoringFilterScope;
  lockedDeviceId?: string;
}

const ApplicationsPage: React.FC<ApplicationsPageProps> = ({
  scope = 'global',
  lockedDeviceId,
}) => {
  const [filters, setFilters] = useMonitoringFilterQuery(scope, lockedDeviceId);

  const { data, isLoading } = useQuery({
    queryKey: ['monitoringApplications', scope, filters],
    queryFn: () => apiClient.monitoring.getApplications({
      date: filters.date,
      employee_id: filters.employeeId || undefined,
      device_id: filters.deviceId || undefined,
      campaign_id: filters.campaignId || undefined,
      application: filters.application || undefined,
    }),
  });

  const [sortBy, setSortBy] = React.useState('active_duration_seconds');
  const [sortDir, setSortDir] = React.useState<SortDir>('desc');
  const rows = sortRows(data?.data?.data || [], sortBy, sortDir, (row: any, key) => {
    if (key === 'first_seen' || key === 'last_seen') return row[key] ? new Date(row[key]).getTime() : 0;
    return row[key];
  });
  const emptyMessage = scope === 'computer'
    ? 'No application usage recorded for this computer.'
    : 'No application usage recorded.';
  const onSort = (column: string) => {
    const next = toggleSort(sortBy, sortDir, column);
    setSortBy(next.key);
    setSortDir(next.dir);
  };

  const exportCsv = () => {
    const params = new URLSearchParams({ date: filters.date });
    if (filters.employeeId) params.set('employee_id', filters.employeeId);
    if (filters.deviceId) params.set('device_id', filters.deviceId);
    if (filters.campaignId) params.set('campaign_id', filters.campaignId);
    window.location.href = `/api/monitoring/export/applications?${params.toString()}`;
  };

  const colSpan = scope === 'global' ? 8 : 7;

  return (
    <Box>
      <MonitoringFilters
        scope={scope}
        value={filters}
        onChange={setFilters}
        lockedDeviceId={lockedDeviceId}
        showApplication
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Time spent per application. The Activity tab is the chronological feed (apps and websites together); this tab is the rollup.
      </Typography>
      <Box mb={2}>
        <Button variant="outlined" size="small" onClick={exportCsv}>Export CSV</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableHeader label="Application" column="application" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Employee" column="employee_name" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Campaign" column="campaign" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              {scope === 'global' && (
                <SortableHeader label="Computer" column="computer" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              )}
              <SortableHeader label="Active Duration" column="active_duration_seconds" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Sessions" column="sessions" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="First Seen" column="first_seen" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
              <SortableHeader label="Last Seen" column="last_seen" activeColumn={sortBy} direction={sortDir} onSort={onSort} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={colSpan}><CircularProgress size={20} /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row: any, index: number) => (
              <TableRow key={`${row.application}-${row.employee_id}-${row.device_id}-${index}`}>
                <TableCell>{row.application}</TableCell>
                <TableCell>{row.employee_name || '—'}</TableCell>
                <TableCell>{row.campaign || '—'}</TableCell>
                {scope === 'global' && <TableCell>{row.computer || '—'}</TableCell>}
                <TableCell>{row.active_duration_label}</TableCell>
                <TableCell>{row.sessions}</TableCell>
                <TableCell>{row.first_seen ? moment(row.first_seen).format('h:mm A') : '—'}</TableCell>
                <TableCell>{row.last_seen ? moment(row.last_seen).format('h:mm A') : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ApplicationsPage;
